import type { TrackDef } from "./track";

/**
 * Circuits designed with the A/B balance simulation (see CHANGELOG 1.35.0). The admin adds them with one button
 * (names already present are skipped), so the permanent circuit indices of an existing server are never disturbed.
 * MIRABEL: long straights between short chicanes, where type A's stronger jet and bigger tank pay off.
 * TREMBLANT: continuous sweeping S-bends with almost no straight, where the jet is useless and type B's top speed counts.
 * MONTREAL: a mixed lap, even between the two.
 * LAURENTIDE: rolling hills that also act on the driving (1.37.0).
 * QUEBEC: the hometown circuit, with the Quebec City backdrop and driving hills (1.38.0).
 * TORONTO: flat street circuit along the lake, with the CN Tower, the Rogers Centre and the downtown skyline.
 * ROME: the seven hills (driving hills) around the Colosseum, with the Pantheon, the Vittoriano and Trajan's Column; A/B gap 0.1 %.
 * PARIS: Champs-Elysees straight, the Etoile roundabout and the quays, with the Eiffel Tower, Notre-Dame, the Arc de Triomphe and Sacre-Coeur (balanced by tools/balance.ts: A and B within 0.5 %).
 * MANX TT: the Isle of Man Mountain Course for the motorbikes (1.79.1): fast flowing bends, no real straight, a long climb over the mountain that acts on the driving.
 * CHARLEVOIX: the first point-to-point stage (rally style, 1.88.0): a mountain road from the start to a finish line far away, hills that act on the driving, and a scenery that changes on the way (forest, mountains, the river).
 * LAPONIE, SAFARI, MONTECARLO and CORSE: four more stages with their own rhythm (fast and flowing with crests; long straights and big bends; tight twisty climbs; a coastal road over mountains), each with changing scenery (1.95.0).
 * RIO, RAPA NUI, HAWAII and TOKYO: new backdrops (Corcovado with Christ the Redeemer and Sugarloaf; the moai of Easter Island; a shield volcano with lava; Tokyo Tower, Skytree, Fuji and a bullet train), driving hills on Rio and Hawaii.
 * LONATO: a short karting track (5 laps) of tight bends, for the kart class (1.79.2).
 */
export const TRACK_PACK: Omit<TrackDef, "id">[] = [
  {
    name: "MIRABEL", country: "CAN", scenery: "airport", lapUnits: 393216, laps: 2,
    curves: [
      { s0: 0.12, s1: 0.157, dir: 1 },
      { s0: 0.29, s1: 0.326, dir: 1 },
      { s0: 0.43, s1: 0.466, dir: 1 },
      { s0: 0.62, s1: 0.656, dir: 1 },
      { s0: 0.8, s1: 0.836, dir: 1 },
    ],
  },
  {
    name: "TREMBLANT", country: "CAN", scenery: "mountains", lapUnits: 786432, laps: 2,
    curves: [
      { s0: 0.05, s1: 0.14, dir: -1 },
      { s0: 0.17, s1: 0.27, dir: -1 },
      { s0: 0.3, s1: 0.55, dir: 1 },
      { s0: 0.58, s1: 0.9, dir: 1 },
    ],
  },
  {
    name: "MONTREAL", country: "CAN", scenery: "montreal", lapUnits: 655360, laps: 2,
    curves: [
      { s0: 0.072, s1: 0.104, dir: 1 },
      { s0: 0.143, s1: 0.548, dir: 1 },
      { s0: 0.592, s1: 0.657, dir: 1 },
      { s0: 0.702, s1: 0.912, dir: -1 },
    ],
  },
  {
    name: "LAURENTIDE", country: "CAN", scenery: "mountains", lapUnits: 655360, laps: 2,
    curves: [
      { s0: 0.06, s1: 0.12, dir: 1 },
      { s0: 0.26, s1: 0.34, dir: -1 },
      { s0: 0.4, s1: 0.52, dir: 1 },
      { s0: 0.56, s1: 0.66, dir: 1 },
      { s0: 0.7, s1: 0.78, dir: -1 },
      { s0: 0.86, s1: 0.95, dir: 1 },
    ],
    hillPhysics: true,
    hills: [
      { s0: 0.1, s1: 0.2, h: 22 },
      { s0: 0.28, s1: 0.38, h: -14 },
      { s0: 0.48, s1: 0.6, h: 28 },
      { s0: 0.74, s1: 0.86, h: 18 },
    ],
  },
  {
    name: "QUEBEC", country: "CAN", scenery: "quebec", lapUnits: 589824, laps: 2,
    curves: [
      { s0: 0.06, s1: 0.15, dir: 1 },
      { s0: 0.2, s1: 0.28, dir: -1 },
      { s0: 0.36, s1: 0.55, dir: 1 },
      { s0: 0.62, s1: 0.7, dir: -1 },
      { s0: 0.76, s1: 0.92, dir: 1 },
    ],
    hillPhysics: true,
    hills: [
      { s0: 0.06, s1: 0.18, h: 20 },
      { s0: 0.26, s1: 0.38, h: -16 },
      { s0: 0.52, s1: 0.66, h: 24 },
      { s0: 0.78, s1: 0.9, h: -18 },
    ],
  },
  {
    name: "PARIS", country: "FRA", scenery: "paris", lapUnits: 655360, laps: 2,
    curves: [
      { s0: 0.1, s1: 0.16, dir: 1 },
      { s0: 0.21, s1: 0.42, dir: 1 },
      { s0: 0.48, s1: 0.54, dir: -1 },
      { s0: 0.58, s1: 0.64, dir: -1 },
      { s0: 0.7, s1: 0.8, dir: 1 },
      { s0: 0.86, s1: 0.92, dir: -1 },
    ],
  },
  {
    name: "TORONTO", country: "CAN", scenery: "toronto", lapUnits: 622592, laps: 2,
    curves: [
      { s0: 0.08, s1: 0.14, dir: 1 },
      { s0: 0.2, s1: 0.26, dir: 1 },
      { s0: 0.32, s1: 0.46, dir: 1 },
      { s0: 0.52, s1: 0.58, dir: -1 },
      { s0: 0.64, s1: 0.7, dir: 1 },
      { s0: 0.76, s1: 0.84, dir: -1 },
      { s0: 0.9, s1: 0.95, dir: 1 },
    ],
  },
  {
    name: "ROME", country: "ITA", scenery: "rome", lapUnits: 589824, laps: 2,
    curves: [
      { s0: 0.07, s1: 0.13, dir: -1 },
      { s0: 0.18, s1: 0.3, dir: 1 },
      { s0: 0.34, s1: 0.4, dir: 1 },
      { s0: 0.46, s1: 0.52, dir: -1 },
      { s0: 0.56, s1: 0.74, dir: 1 },
      { s0: 0.8, s1: 0.86, dir: -1 },
      { s0: 0.9, s1: 0.95, dir: 1 },
    ],
    hillPhysics: true,
    hills: [
      { s0: 0.1, s1: 0.2, h: 18 },
      { s0: 0.28, s1: 0.4, h: -14 },
      { s0: 0.5, s1: 0.62, h: 22 },
      { s0: 0.72, s1: 0.84, h: -16 },
    ],
  },
  {
    name: "MANX TT", country: "IMN", scenery: "manx", lapUnits: 786432, laps: 2,
    curves: [
      { s0: 0.05, s1: 0.1, dir: 1 },
      { s0: 0.13, s1: 0.19, dir: -1 },
      { s0: 0.22, s1: 0.3, dir: 1 },
      { s0: 0.33, s1: 0.37, dir: -1 },
      { s0: 0.4, s1: 0.5, dir: 1 },
      { s0: 0.54, s1: 0.58, dir: -1 },
      { s0: 0.62, s1: 0.72, dir: 1 },
      { s0: 0.75, s1: 0.8, dir: 1 },
      { s0: 0.84, s1: 0.88, dir: -1 },
      { s0: 0.91, s1: 0.96, dir: 1 },
    ],
    hillPhysics: true,
    hills: [
      { s0: 0.15, s1: 0.3, h: 20 },
      { s0: 0.34, s1: 0.46, h: 30 },
      { s0: 0.5, s1: 0.6, h: -18 },
      { s0: 0.66, s1: 0.78, h: 26 },
      { s0: 0.82, s1: 0.94, h: -22 },
    ],
  },
  {
    name: "LONATO", country: "ITA", scenery: "karting", lapUnits: 262144, laps: 5,
    curves: [
      { s0: 0.05, s1: 0.11, dir: 1 },
      { s0: 0.14, s1: 0.19, dir: -1 },
      { s0: 0.22, s1: 0.29, dir: 1 },
      { s0: 0.32, s1: 0.38, dir: 1 },
      { s0: 0.42, s1: 0.47, dir: -1 },
      { s0: 0.5, s1: 0.58, dir: 1 },
      { s0: 0.61, s1: 0.66, dir: -1 },
      { s0: 0.69, s1: 0.76, dir: 1 },
      { s0: 0.79, s1: 0.85, dir: 1 },
      { s0: 0.88, s1: 0.93, dir: -1 },
      { s0: 0.94, s1: 0.97, dir: 1 },
    ],
  },
  {
    name: "CHARLEVOIX", country: "CAN", scenery: "forest", lapUnits: 1572864, laps: 1, stage: true, hillPhysics: true,
    zones: [{ s0: 0.34, s1: 0.7, scenery: "mountains" }, { s0: 0.7, s1: 1, scenery: "seaside" }], // forest, then the mountains, then the St. Lawrence
    curves: [
      { s0: 0.048, s1: 0.092, dir: 1 },
      { s0: 0.118, s1: 0.173, dir: -1 },
      { s0: 0.198, s1: 0.253, dir: -1 },
      { s0: 0.288, s1: 0.343, dir: 1 },
      { s0: 0.379, s1: 0.412, dir: 1 },
      { s0: 0.437, s1: 0.503, dir: -1 },
      { s0: 0.538, s1: 0.582, dir: 1 },
      { s0: 0.597, s1: 0.653, dir: 1 },
      { s0: 0.687, s1: 0.743, dir: -1 },
      { s0: 0.769, s1: 0.802, dir: -1 },
      { s0: 0.827, s1: 0.904, dir: 1 },
      { s0: 0.918, s1: 0.962, dir: -1 },
    ],
    hills: [
      { s0: 0.1, s1: 0.22, h: 14 },
      { s0: 0.3, s1: 0.42, h: -12 },
      { s0: 0.5, s1: 0.64, h: 18 },
      { s0: 0.7, s1: 0.82, h: -14 },
    ],
  },
  {
    // LAPONIE: fast and flowing through the northern forest, with a crest every few seconds that lifts the car
    name: "LAPONIE", country: "FIN", scenery: "forest", lapUnits: 1835008, laps: 1, stage: true, hillPhysics: true,
    zones: [{ s0: 0.36, s1: 0.7, scenery: "hills" }, { s0: 0.7, s1: 1, scenery: "trees" }],
    curves: [
      { s0: 0.05, s1: 0.1, dir: 1 },
      { s0: 0.13, s1: 0.19, dir: -1 },
      { s0: 0.22, s1: 0.26, dir: 1 },
      { s0: 0.3, s1: 0.36, dir: -1 },
      { s0: 0.39, s1: 0.43, dir: -1 },
      { s0: 0.47, s1: 0.54, dir: 1 },
      { s0: 0.58, s1: 0.62, dir: -1 },
      { s0: 0.66, s1: 0.72, dir: 1 },
      { s0: 0.76, s1: 0.8, dir: 1 },
      { s0: 0.84, s1: 0.9, dir: -1 },
      { s0: 0.92, s1: 0.96, dir: 1 },
    ],
    hills: [
      { s0: 0.08, s1: 0.18, h: 10 },
      { s0: 0.2, s1: 0.3, h: -10 },
      { s0: 0.34, s1: 0.44, h: 14 },
      { s0: 0.46, s1: 0.56, h: -14 },
      { s0: 0.6, s1: 0.7, h: 12 },
      { s0: 0.72, s1: 0.82, h: -12 },
      { s0: 0.86, s1: 0.95, h: 8 },
    ],
  },
  {
    // SAFARI: the long savanna straights of the East African rally, a few big sweeping bends, then the dry desert plain
    name: "SAFARI", country: "KEN", scenery: "savanna", lapUnits: 2621440, laps: 1, stage: true, hillPhysics: true,
    zones: [{ s0: 0.55, s1: 1, scenery: "desert" }],
    curves: [
      { s0: 0.1, s1: 0.18, dir: 1 },
      { s0: 0.3, s1: 0.4, dir: -1 },
      { s0: 0.45, s1: 0.5, dir: -1 },
      { s0: 0.63, s1: 0.72, dir: 1 },
      { s0: 0.8, s1: 0.85, dir: -1 },
      { s0: 0.9, s1: 0.95, dir: 1 },
    ],
    hills: [
      { s0: 0.2, s1: 0.28, h: 6 },
      { s0: 0.52, s1: 0.62, h: -8 },
      { s0: 0.74, s1: 0.8, h: 10 },
    ],
  },
  {
    // MONTECARLO: a twisty mountain road above the sea, tight alternating bends with steep climbs, ending in the streets of the Principality
    name: "MONTECARLO", country: "MON", scenery: "mountains", lapUnits: 1310720, laps: 1, stage: true, hillPhysics: true,
    zones: [{ s0: 0.45, s1: 0.78, scenery: "seaside" }, { s0: 0.78, s1: 1, scenery: "city" }],
    curves: [
      { s0: 0.05, s1: 0.09, dir: 1 },
      { s0: 0.11, s1: 0.15, dir: -1 },
      { s0: 0.17, s1: 0.22, dir: 1 },
      { s0: 0.25, s1: 0.29, dir: -1 },
      { s0: 0.31, s1: 0.36, dir: -1 },
      { s0: 0.39, s1: 0.43, dir: 1 },
      { s0: 0.46, s1: 0.5, dir: -1 },
      { s0: 0.53, s1: 0.58, dir: 1 },
      { s0: 0.6, s1: 0.64, dir: 1 },
      { s0: 0.67, s1: 0.71, dir: -1 },
      { s0: 0.74, s1: 0.78, dir: 1 },
      { s0: 0.81, s1: 0.85, dir: -1 },
      { s0: 0.88, s1: 0.92, dir: 1 },
      { s0: 0.94, s1: 0.97, dir: -1 },
    ],
    hills: [
      { s0: 0.06, s1: 0.2, h: 18 },
      { s0: 0.24, s1: 0.4, h: -16 },
      { s0: 0.44, s1: 0.58, h: 20 },
      { s0: 0.62, s1: 0.76, h: -18 },
      { s0: 0.8, s1: 0.94, h: 8 },
    ],
  },
  {
    // CORSE: a coastal road between the cliffs and the sea, medium bends and a long climb over the island's mountains
    name: "CORSE", country: "FRA", scenery: "seaside", lapUnits: 1441792, laps: 1, stage: true, hillPhysics: true,
    zones: [{ s0: 0.3, s1: 0.65, scenery: "mountains" }, { s0: 0.65, s1: 1, scenery: "seaside" }],
    curves: [
      { s0: 0.06, s1: 0.12, dir: -1 },
      { s0: 0.15, s1: 0.2, dir: 1 },
      { s0: 0.24, s1: 0.3, dir: 1 },
      { s0: 0.34, s1: 0.38, dir: -1 },
      { s0: 0.42, s1: 0.5, dir: -1 },
      { s0: 0.54, s1: 0.58, dir: 1 },
      { s0: 0.62, s1: 0.68, dir: 1 },
      { s0: 0.72, s1: 0.76, dir: -1 },
      { s0: 0.8, s1: 0.86, dir: -1 },
      { s0: 0.9, s1: 0.95, dir: 1 },
    ],
    hills: [
      { s0: 0.1, s1: 0.28, h: 12 },
      { s0: 0.32, s1: 0.5, h: 16 },
      { s0: 0.52, s1: 0.7, h: -20 },
      { s0: 0.74, s1: 0.9, h: 6 },
    ],
  },
  {
    name: "RIO", country: "BRA", scenery: "rio", lapUnits: 655360, laps: 2,
    curves: [
      { s0: 0.07, s1: 0.13, dir: 1 },
      { s0: 0.18, s1: 0.3, dir: 1 },
      { s0: 0.36, s1: 0.42, dir: -1 },
      { s0: 0.48, s1: 0.56, dir: 1 },
      { s0: 0.62, s1: 0.68, dir: -1 },
      { s0: 0.74, s1: 0.82, dir: 1 },
      { s0: 0.88, s1: 0.94, dir: -1 },
    ],
    hillPhysics: true,
    hills: [
      { s0: 0.12, s1: 0.26, h: 14 },
      { s0: 0.3, s1: 0.46, h: -18 },
      { s0: 0.5, s1: 0.64, h: 22 },
      { s0: 0.7, s1: 0.86, h: -14 },
    ],
  },
  {
    name: "RAPA NUI", country: "CHL", scenery: "paques", lapUnits: 655360, laps: 2,
    curves: [
      { s0: 0.12, s1: 0.2, dir: 1 },
      { s0: 0.27, s1: 0.33, dir: -1 },
      { s0: 0.38, s1: 0.42, dir: 1 },
      { s0: 0.48, s1: 0.6, dir: 1 },
      { s0: 0.66, s1: 0.72, dir: -1 },
      { s0: 0.78, s1: 0.82, dir: 1 },
      { s0: 0.87, s1: 0.94, dir: 1 },
    ],
    hills: [
      { s0: 0.2, s1: 0.38, h: 8 },
      { s0: 0.6, s1: 0.78, h: -10 },
    ],
  },
  {
    name: "HAWAII", country: "USA", scenery: "hawaii", lapUnits: 655360, laps: 2,
    curves: [
      { s0: 0.08, s1: 0.16, dir: 1 },
      { s0: 0.22, s1: 0.28, dir: -1 },
      { s0: 0.36, s1: 0.46, dir: 1 },
      { s0: 0.54, s1: 0.6, dir: 1 },
      { s0: 0.68, s1: 0.74, dir: -1 },
      { s0: 0.82, s1: 0.92, dir: 1 },
    ],
    hillPhysics: true,
    hills: [
      { s0: 0.1, s1: 0.3, h: 16 },
      { s0: 0.34, s1: 0.5, h: -20 },
      { s0: 0.54, s1: 0.72, h: 24 },
      { s0: 0.76, s1: 0.9, h: -14 },
    ],
  },
  {
    name: "TOKYO", country: "JPN", scenery: "tokyo", lapUnits: 458752, laps: 3,
    curves: [
      { s0: 0.06, s1: 0.12, dir: 1 },
      { s0: 0.16, s1: 0.22, dir: 1 },
      { s0: 0.28, s1: 0.34, dir: -1 },
      { s0: 0.4, s1: 0.5, dir: 1 },
      { s0: 0.56, s1: 0.62, dir: -1 },
      { s0: 0.68, s1: 0.76, dir: 1 },
      { s0: 0.82, s1: 0.88, dir: 1 },
      { s0: 0.92, s1: 0.96, dir: -1 },
    ],
  },
];
